class detailSelect {
    constructor(container) {
      this.container = container;
      this.options = container.querySelectorAll('.we-select > .we-select__item');
      const checkedInput = container.querySelector('input:checked');
      this.value = checkedInput ? checkedInput.value : (this.options[0]?.querySelector('input')?.value ?? '');
      this.mouseDown = false;
      this._addEventListeners();
      this._setAria();
      this._setInitialActive();
      this.updateValue();
      
      // Update variant availability after a short delay to ensure Shopify's variant system is ready
      setTimeout(() => {
        this.updateVariantAvailability();
      }, 100);
    }
  
    // Private function to set event listeners
    _addEventListeners() {
      this.container.addEventListener('toggle', () => {
        if (this.container.open) return;
        this.updateValue();
      })

      this.container.addEventListener('focusout', e => {
        if (this.mouseDown) return;
        this.container.removeAttribute('open');
      })

      this.options.forEach(opt => {
        const input = opt.querySelector('input');
        if (input) {
          input.addEventListener('change', () => {
            this.setChecked(input);
          });
        }
        opt.addEventListener('mousedown', () => {
          this.mouseDown = true;
        })
        opt.addEventListener('mouseup', () => {
          this.mouseDown = false;
          this.container.removeAttribute('open');
        })
      })

      this.container.addEventListener('keyup', e => {
        const keycode = e.which;
        const current = [...this.options].indexOf(this.container.querySelector('.active'));
        switch (keycode) {
          case 27: // ESC
            this.container.removeAttribute('open');
            break;
          case 35: // END
            e.preventDefault();
            if (!this.container.open) this.container.setAttribute('open', '');
            this.setChecked(this.options[this.options.length - 1].querySelector('input'))
            break;
          case 36: // HOME
            e.preventDefault();
            if (!this.container.open) this.container.setAttribute('open', '');
            this.setChecked(this.options[0].querySelector('input'))
            break;
          case 38: // UP
            e.preventDefault();
            if (!this.container.open) this.container.setAttribute('open', '');
            this.setChecked(this.options[current > 0 ? current - 1 : 0].querySelector('input'));
            break;
          case 40: // DOWN
            e.preventDefault();
            if (!this.container.open) this.container.setAttribute('open', '');
            this.setChecked(this.options[current < this.options.length - 1 ? current + 1 : this.options.length - 1].querySelector('input'));
            break;
        }
      })
      
      // Listen for variant changes from other parts of the system to keep our select in sync
      document.addEventListener('variant:change', (e) => {
        if (e.detail && e.detail.optionName === this.container.dataset.optionName) {
          this.syncWithVariant(e.detail.value);
        }
      });
      
      // Listen for Shopify's variant change events
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        this.variantChangeUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
          // Update variant availability when variants change
          setTimeout(() => {
            this.updateVariantAvailability();
          }, 50);
        });
      }
    }

    // Clean up event listeners when the component is destroyed
    destroy() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
    }

    // Sync the custom select with a specific variant value
    syncWithVariant(value) {
      const input = this.container.querySelector(`input[value="${value}"]`);
      if (input && input !== this.container.querySelector('input:checked')) {
        this.setChecked(input);
      }
    }

    // Update variant availability based on Shopify's variant data
    updateVariantAvailability() {
      // Find the variant-selects element to get variant data
      let variantSelects = this.container.closest('variant-selects');
      if (!variantSelects) {
        const section = this.container.closest('[data-section]');
        if (section) {
          variantSelects = section.querySelector('variant-selects');
        }
      }
      
      if (variantSelects && variantSelects.variantData) {
        const variantData = variantSelects.variantData;
        const optionName = this.container.dataset.optionName;
        
        // Update availability for each option
        this.options.forEach(option => {
          const input = option.querySelector('input');
          const value = input.value;
          
          // Check if this option value is available with current selections
          const isAvailable = variantData.some(variant => {
            // Find the option index for this option name
            const optionIndex = this.getOptionIndex(optionName);
            if (optionIndex === -1) return true;
            
            return variant[`option${optionIndex + 1}`] === value;
          });
          
          if (isAvailable) {
            input.classList.remove('disabled');
            input.disabled = false;
          } else {
            input.classList.add('disabled');
            input.disabled = true;
          }
        });
      }
    }
    
    // Get the option index (0-based) for a given option name
    getOptionIndex(optionName) {
      const variantSelects = this.container.closest('variant-selects') ||
                            this.container.closest('[data-section]')?.querySelector('variant-selects');
      
      if (variantSelects) {
        const containers = variantSelects.querySelectorAll('.we-select-container');
        for (let i = 0; i < containers.length; i++) {
          if (containers[i].dataset.optionName === optionName) {
            return i;
          }
        }
      }
      return -1;
    }
  
    _setAria() {
      const weSelect = this.container.querySelector('.we-select');
      if (weSelect) weSelect.setAttribute('role', 'listbox');
      const summary = this.container.querySelector('.we-select-container__summary');
      if (summary) {
        summary.setAttribute('aria-label', summary.getAttribute('aria-label') || 'listbox');
        summary.setAttribute('aria-live', 'polite');
      }
      this.container.setAttribute('aria-haspopup', 'listbox');
      this.options.forEach(opt => {
        opt.setAttribute('role', 'option');
      });
    }

    _setInitialActive() {
      const checked = this.container.querySelector('input:checked');
      if (checked) {
        this.options.forEach(opt => {
          opt.classList.remove('active');
          opt.setAttribute('aria-selected', 'false');
        });
        checked.closest('.we-select__item')?.classList.add('active');
        checked.closest('.we-select__item')?.setAttribute('aria-selected', 'true');
      }
    }
  
    updateValue() {
      const that = this.container.querySelector('input:checked');
      if (!that) return;
      this.setValue(that);
    }
  
    setChecked(that) {
      that.checked = true;
      this.setValue(that)
    }
  
    setValue(that) {
      if (!that || this.value == that.value) return;

      const summary = this.container.querySelector('.we-select-container__summary');
      const label = that.closest('.we-select__item')?.querySelector('label');
      const pos = [...this.options].indexOf(that.closest('.we-select__item')) + 1;
      if (summary && label) {
        const selectedText = label.textContent.trim();
        const summaryValueSpan = summary.querySelector('.we-select-container__summary-value');
        if (summaryValueSpan) {
          summaryValueSpan.textContent = selectedText;
        } else {
          summary.innerHTML = label.innerHTML;
        }
        const labelAbove = this.container.parentElement?.querySelector('[data-selected-swatch-value]');
        if (labelAbove) {
          labelAbove.textContent = selectedText;
        }
      }
      summary.setAttribute('aria-label', `${that.value}, listbox ${pos} of ${this.options.length}`);
      this.value = that.value;

      this.options.forEach(opt => {
        opt.classList.remove('active');
        opt.setAttribute('aria-selected', 'false');
      })
      that.parentNode.classList.add('active');
      that.parentNode.setAttribute('aria-selected', 'true');

      // Dispatch the change event for the container
      this.container.dispatchEvent(new Event('change'));
      
      // Only trigger variant-selects if this dropdown is actually inside one (main product form).
      // Do NOT look for variant-selects in the section or globally, or we'd match the main
      // product's variant-selects when this is a card dropdown in the same section.
      const variantSelects = this.container.closest('variant-selects');
      let variantChangeValue = that.value;

      if (variantSelects) {
        // Trigger the change event on the variant-selects element so product-info fetches
        // and updates price, SKU, inventory, etc. via handleOptionValueChange
        variantSelects.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // Card context: update the product card's price display and bundle-product variant id
        const productId = this.container.getAttribute('data-product-id') || this.container.dataset?.productId;
        const card = this.container.closest('.product-card');
        const variantsJsonEl = productId ? document.getElementById('product-card-variants-' + productId) : null;
        let cardVariantIdForEvent = that.value;

        if (variantsJsonEl) {
          // Option-based card: resolve variant from selected option values
          let variants;
          try {
            variants = JSON.parse(variantsJsonEl.textContent || '[]');
          } catch (e) {
            variants = [];
          }
          const containers = card ? card.querySelectorAll('.we-select-container[data-option-position]') : [];
          const selectedValues = [];
          containers.forEach((c) => {
            const pos = parseInt(c.getAttribute('data-option-position'), 10) || 0;
            const input = c.querySelector('input:checked');
            selectedValues[pos - 1] = input ? input.value : null;
          });
          const variant = variants.find((v) => {
            const o1 = (v.option1 || '') === (selectedValues[0] || '');
            const o2 = (v.option2 || '') === (selectedValues[1] || '');
            const o3 = (v.option3 || '') === (selectedValues[2] || '');
            return o1 && o2 && o3;
          });

          if (variant) {
            cardVariantIdForEvent = String(variant.id);
            variantChangeValue = cardVariantIdForEvent;
            const priceEl = productId
              ? document.getElementById('product-card-price-' + productId)
              : card?.querySelector('.product-card__price-wrapper');
            if (priceEl) {
              const format = (typeof FoxTheme !== 'undefined' && FoxTheme.settings && FoxTheme.settings.moneyFormat) ? FoxTheme.settings.moneyFormat : undefined;
              const formatted = (typeof FoxTheme !== 'undefined' && FoxTheme.Currency && FoxTheme.Currency.formatMoney) ? FoxTheme.Currency.formatMoney(variant.price, format) : String(variant.price / 100);
              const priceWithPlus = '+ ' + formatted;
              const priceItems = priceEl.querySelectorAll('.f-price-item--regular, .f-price-item--sale');
              if (priceItems.length) {
                priceItems.forEach(function (node) { node.textContent = priceWithPlus; });
              } else {
                priceEl.textContent = priceWithPlus;
              }
            }
            const bundleProduct = this.container.closest('.bundle-product');
            if (bundleProduct) {
              bundleProduct.dataset.variantId = cardVariantIdForEvent;
            }
          }
        } else {
          // Single dropdown with variant id per option (legacy / single-variant list)
          const selectedItem = that.closest('.we-select__item');
          const variantPrice = selectedItem
            ? (selectedItem.getAttribute('data-variant-price') || selectedItem.dataset?.variantPrice)
            : null;

          if (variantPrice) {
            const priceEl = productId
              ? document.getElementById('product-card-price-' + productId)
              : this.container.closest('.product-card')?.querySelector('.product-card__price-wrapper');
            if (priceEl) {
              const displayPrice = variantPrice.trim().replace(/^\+\s*/, '');
              const priceWithPlus = '+ ' + displayPrice;
              const priceItems = priceEl.querySelectorAll('.f-price-item--regular, .f-price-item--sale');
              if (priceItems.length) {
                priceItems.forEach(function (node) { node.textContent = priceWithPlus; });
              } else {
                priceEl.textContent = priceWithPlus;
              }
            }
          }
          if (productId) {
            const bundleProduct = this.container.closest('.bundle-product');
            if (bundleProduct) {
              bundleProduct.dataset.variantId = that.value;
            }
          }
        }

        // Also trigger the pubsub variant-change event if available (card context)
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.variantChange, {
            target: this.container,
            value: variantChangeValue,
            optionName: this.container.dataset.optionName
          });
        }

        // Trigger a custom event that other parts of the system might be listening for (card context)
        this.container.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            target: this.container,
            value: variantChangeValue,
            optionName: this.container.dataset.optionName
          }
        }));
      }

      // Also trigger the pubsub variant-change event if available (main product variant-selects path)
      if (variantSelects && typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.variantChange, {
          target: this.container,
          value: variantChangeValue,
          optionName: this.container.dataset.optionName
        });
      }
      if (variantSelects) {
        this.container.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            target: this.container,
            value: variantChangeValue,
            optionName: this.container.dataset.optionName
          }
        }));
      }
      
      // Close the details element after selection
      this.container.removeAttribute('open');
    }
  }

// Make the class globally available
window.detailSelect = detailSelect;

// Initialize all detail select components when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Store references to all detailSelect instances
  window.detailSelectInstances = [];
  
  document.querySelectorAll('.we-select-container').forEach(container => {
    const instance = new detailSelect(container);
    window.detailSelectInstances.push(instance);
  });
});

// Clean up function that can be called if needed
window.cleanupDetailSelects = function() {
  if (window.detailSelectInstances) {
    window.detailSelectInstances.forEach(instance => {
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
    window.detailSelectInstances = [];
  }
};

